/**
 * Aba Dependentes do funcionário (U5): cadastro detalhado por pessoa
 * (nome/nascimento/IR/SF) com adicionar/remover — a quantidade para o IR deriva
 * dos registros; quando não há detalhe, vale a quantidade manual (fallback
 * exibido com transparência).
 */
import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Group, Text, TextInput, Title } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { api } from "../../api";
import { fmtData } from "../../format";
import { DataField } from "../../componentes/entradas-mantine";
import { notificarOk, notificarErroApi } from "../../componentes/notificacoes";
import { Carregando } from "../../componentes/Estados";

interface DependenteDetalhado {
  id: string;
  nome: string;
  nascimento: string | null;
  paraIrrf: boolean;
  paraSalarioFamilia: boolean;
}

export function DependentesTab({ funcionarioId, quantidadeManual }: { funcionarioId: string; quantidadeManual: number }) {
  const [dados, setDados] = useState<{ detalhados: DependenteDetalhado[]; quantidadeManual: number; efetivoIrrf: number } | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [nome, setNome] = useState("");
  const [nascimento, setNascimento] = useState<string | null>(null);
  const [paraIrrf, setParaIrrf] = useState(true);
  const [paraSf, setParaSf] = useState(false);

  async function carregar() {
    try {
      setDados(await api.get(`/funcionarios/${funcionarioId}/dependentes-detalhados`));
    } catch (e) {
      setErro(e);
    }
  }
  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funcionarioId]);

  async function adicionar() {
    try {
      await api.post(`/funcionarios/${funcionarioId}/dependentes-detalhados`, {
        nome: nome.trim(),
        nascimento,
        paraIrrf,
        paraSalarioFamilia: paraSf,
      });
      notificarOk("Dependente adicionado — a contagem para o IR já derivou.");
      setNome("");
      setNascimento(null);
      setParaIrrf(true);
      setParaSf(false);
      await carregar();
    } catch (e) {
      notificarErroApi(e);
    }
  }

  async function remover(id: string) {
    try {
      await api.del(`/dependentes-detalhados/${id}`);
      notificarOk("Dependente removido.");
      await carregar();
    } catch (e) {
      notificarErroApi(e);
    }
  }

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!dados) return <Carregando linhas={3} />;

  return (
    <Card withBorder shadow="xs" maw={640}>
      <Title order={5} mb="sm">
        Dependentes — {dados.efetivoIrrf} para o IR
      </Title>
      {dados.detalhados.length === 0 ? (
        <Text c="dimmed" size="sm" mb="sm">
          Sem cadastro detalhado — hoje vale a quantidade manual: <strong>{quantidadeManual}</strong> para o IR (fallback;
          cadastre abaixo para passar a derivar).
        </Text>
      ) : (
        dados.detalhados.map((d) => (
          <Group key={d.id} justify="space-between" mb={4}>
            <Text size="sm">
              <strong>{d.nome}</strong> · {d.nascimento ? fmtData(d.nascimento) : "sem nascimento"} · IR {d.paraIrrf ? "sim" : "não"}
              {d.paraSalarioFamilia ? " · salário-família" : ""}
            </Text>
            <Button variant="subtle" color="red" size="xs" onClick={() => void remover(d.id)} aria-label={`Remover ${d.nome}`}>
              <IconTrash size={14} />
            </Button>
          </Group>
        ))
      )}
      <Group align="flex-end" mt="md" wrap="wrap">
        <TextInput label="Nome" value={nome} onChange={(e) => setNome(e.currentTarget.value)} w={220} />
        <DataField label="Nascimento" valor={nascimento} onChange={setNascimento} w={150} />
        <Checkbox label="IR" checked={paraIrrf} onChange={(e) => setParaIrrf(e.currentTarget.checked)} />
        <Checkbox label="Salário-família" checked={paraSf} onChange={(e) => setParaSf(e.currentTarget.checked)} />
        <Button leftSection={<IconPlus size={14} />} variant="light" onClick={() => void adicionar()} disabled={!nome.trim()}>
          adicionar
        </Button>
      </Group>
    </Card>
  );
}
